import { useEffect, useState, type ReactNode } from 'react'
import { categoryPalette, layout, neutrals, spacingScale } from './design/tokens'

/**
 * Page technique du socle v0.1 — elle rend les jetons de la section 03 pour qu'on vérifie à
 * l'œil ce que les tests vérifient au calcul, et elle sonde l'API.
 *
 * Ce n'est pas de l'interface produit : aucune planche du cahier des charges n'est reproduite
 * ici. Les écrans du Lot 1 remplaceront cette page.
 */
export function Foundations() {
  const api = useApiStatus()

  return (
    <main
      className="mx-auto bg-surface py-8"
      style={{ maxWidth: layout.column, paddingInline: layout.gutter }}
    >
      <p className="text-label font-semibold uppercase text-ink-muted">
        Socle v0.1 · page technique
      </p>
      <h1 className="mt-2 font-serif text-screen">Fondations</h1>
      <p className="mt-4 text-body text-ink-muted">
        Jetons de la section 03 du cahier des charges. Aucun écran produit n'est encore
        implémenté.
      </p>

      <Section title="API">
        <div
          className="rounded-card border border-border bg-card p-4 shadow-card"
          style={{ minHeight: layout.minimumTouchTarget }}
        >
          <p className="text-card-title font-semibold">{api.label}</p>
          <p className="mt-1 text-meta text-ink-muted">{api.detail}</p>
        </div>
      </Section>

      <Section title="Catégories">
        <ul className="grid grid-cols-2" style={{ gap: layout.gridGap }}>
          {categoryPalette.map((category) => (
            <li
              key={category.hue}
              className="rounded-card border border-border p-4"
              style={{ background: category.fill }}
            >
              <span
                className="flex items-center justify-center rounded-field text-card-title font-semibold"
                style={{ width: 40, height: 40, background: category.mark, color: neutrals.card }}
              >
                {category.initials}
              </span>
              <p className="mt-3 text-card-title font-semibold">{category.seedName}</p>
              <p className="mt-1 text-meta text-ink-muted">
                {category.fill} · {category.mark}
              </p>
            </li>
          ))}
        </ul>
      </Section>

      <Section title="Espacement">
        <ul className="flex flex-col gap-2">
          {spacingScale.map((step) => (
            <li key={step} className="flex items-center gap-3">
              <span className="text-meta text-ink-muted" style={{ width: 40 }}>
                {step}
              </span>
              <span className="bg-accent" style={{ width: step * 4, height: 8 }} />
            </li>
          ))}
        </ul>
      </Section>

      <Section title="Typographie">
        <p className="font-serif text-screen">Aujourd'hui</p>
        <p className="mt-3 font-serif text-record font-medium">Tarte aux poireaux et chèvre</p>
        <p className="mt-3 text-card-title font-semibold">Titre de carte, Work Sans 600</p>
        <p className="mt-3 text-body">Texte courant, Work Sans 400, interlignage généreux.</p>
        <p className="mt-3 text-meta text-ink-muted">Métadonnée, plancher absolu de 13 px.</p>
      </Section>

      <Section title="Boutons">
        <button
          type="button"
          className="w-full rounded-field bg-accent text-card-title font-semibold text-card transition-colors hover:bg-accent-hover"
          style={{ height: layout.primaryButtonHeight }}
        >
          Enregistrer
        </button>
        <button
          type="button"
          className="mt-3 w-full rounded-field border border-border bg-card text-card-title font-semibold"
          style={{ height: layout.primaryButtonHeight }}
        >
          Plus tard
        </button>
      </Section>
    </main>
  )
}

function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section className="mt-8">
      <h2 className="text-label font-semibold uppercase text-ink-muted">{title}</h2>
      <div className="mt-3">{children}</div>
    </section>
  )
}

type ApiStatus = { label: string; detail: string }

function useApiStatus(): ApiStatus {
  const [status, setStatus] = useState<ApiStatus>({
    label: 'Vérification…',
    detail: 'GET /health/ready',
  })

  useEffect(() => {
    const controller = new AbortController()

    fetch('/health/ready', { signal: controller.signal })
      .then((response) => {
        setStatus({
          label: response.ok ? 'API disponible' : 'API en échec',
          detail: `GET /health/ready → ${response.status}`,
        })
      })
      .catch((error: unknown) => {
        if (controller.signal.aborted) {
          return
        }
        setStatus({
          label: 'API injoignable',
          detail: error instanceof Error ? error.message : 'Erreur inconnue',
        })
      })

    return () => {
      controller.abort()
    }
  }, [])

  return status
}
